// ANGULAR DEPENDANCY
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

// EXTERNAL DEPENDANCY
import { Observable, of } from 'rxjs';

// SERVICES
import { ApiConfigService } from '../../../core/services/api-config/api-config.service';
import { BaseService } from '../../../core/services/base/base.service';
import { TokenStorageService } from '../../../core/services/token-storage/token-storage.service';

// COMMON INTERFACES
import { ApiPayload } from '../../../../models/common/api/api-payload.interface';
import { ApiResponse } from '../../../../models/common/api/api-response.interface';

// COMMON UTILS
import { APIUtil } from '../../../../core/utils/api-util';

// CONSTANTS
import { DASHBOARD_API_URL } from '../../../../constants/api/dashboard';
import { catchError, map } from 'rxjs/operators';
import { ALERT_MESSAGES } from '../../../../constants/messages/message.constant';
import { ToasterService } from '../../../../core/services/toaster.service';


@Injectable()
export class DashBoardService extends BaseService {

  constructor(
    protected httpClient: HttpClient,
    protected tokenStorage: TokenStorageService,
    private toasterService: ToasterService,
    public apiConfig: ApiConfigService) {
    super(httpClient, tokenStorage);
  }

  /**
    * dashboard common master api
    * @param payload : Api common payload object
    * @returns Observable<ApiResponse>
    */
  public dashboardCommon(payload: ApiPayload): Observable<ApiResponse> {
    const replaceParams = ['type'];
    const url = APIUtil.getReplaceParams(`${this.apiConfig.getApiUrl()}${DASHBOARD_API_URL[payload.data.type]}`, replaceParams, payload);

    return this.post(url, payload.data.data);
  }

  /**
   * Get Dashboards Data with give page no and size
   * @param payload : Api common payload object
   * @returns Observable<ApiResponse>
   */
  public getDashboardsPaging(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl()}${DASHBOARD_API_URL.GET_DASHBOARD_PAGING
      }`;

    return this.post(url, payload);
  }

  /**
   * Get Dashboard Data for the given loopId
   * @param payload : Api common payload object
   * @returns Observable<ApiResponse>
   */
  public getDashboardById(payload: any): Observable<ApiResponse> {
    const replaceParams = ['id'];
    payload.id = payload.dashboard_id ? payload.dashboard_id : null;
    // get url
    const url = APIUtil.getAppendParams(
      `${this.apiConfig.getApiUrl()}${DASHBOARD_API_URL.GET_DASHBOARD_ID}`,
      replaceParams,
      payload,
    );

    return this.get(url);
  }

  /**
   * Get Dashboard Data for the given loopId
   * @param payload : Api common payload object
   * @returns Observable<ApiResponse>
   */
  public getDashboardAll(): any {
    const url = `${this.apiConfig.getApiUrl()}${DASHBOARD_API_URL.GET_ALL_DASHBOARD}`;

    return this.get(url).pipe(
      map((data: any) => {
        return { success: true, data };
      }),
      catchError((data) => {
        return of({ success: false, data });
      })
    )
  }

  public getDashboardAllExport(payload?: any): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl()}${DASHBOARD_API_URL.GET_ALL_DASHBOARD}`;

    return this.get(url);
  }

  public unArchiveDashboard(body, id) {
    const url = `${this.apiConfig.getApiUrl()}${DASHBOARD_API_URL.UNARCHIVE_DASHBOARD}/${id}`;
    return this.put(url, body).pipe(
      map((data: any) => {
        this.toasterService.successShow(ALERT_MESSAGES.UNARCHIVE_SUCCESS, '', { closeButton: true, timeOut: 3000 });
        return { success: true, data };
      }),
      catchError((data) => {
        return of({ success: false, data });
      })
    )
  }


  /**
 * Get Dashboard Data for the given loopId
 * @param payload : Api common payload object
 * @returns Observable<ApiResponse>
 */
  public getDashboardWidgetById(payload: ApiPayload): Observable<ApiResponse> {
    const replaceParams = ['widget_id'];
    // get url
    const url = APIUtil.getReplaceParams(
      `${this.apiConfig.getApiUrl()}${DASHBOARD_API_URL.GET_DASHBOARD_WIDGET_BY_ID}`,
      replaceParams,
      payload,
    );

    return this.get(url);
  }


  /**
   * delete the loop
   * @param payload : Api common payload object
   * @returns Observable<ApiResponse>
   */
  public deleteDashboard(payload: ApiPayload): Observable<ApiResponse> {
    const replaceParams = ['dashboard_id'];
    const url = APIUtil.getReplaceParams(
      `${this.apiConfig.getApiUrl()}${DASHBOARD_API_URL.DELETE_DASHBOARD}`,
      replaceParams,
      payload,
    );

    return this.delete(url);
  }


  /**
 * create the Dashboard
 * @param payload : Api common payload object
 * @returns Observable<ApiResponse>
 */
  public createDashboard(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl()}${DASHBOARD_API_URL.CREATE_DASHBOARD
      }`;

    return this.post(url, payload);
  }


  /**
   * Update the loop
   * @param payload : Api common payload object
   * @returns Observable<ApiResponse>
   */
  public updateDashboard(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl()}${DASHBOARD_API_URL.UPDATE_DASHBOARD
      }`;

    return this.put(url, payload);
  }

  public setDefaultBusiness(payload: ApiPayload): Observable<ApiResponse> {

    const replaceParams = ['id'];
    const url = APIUtil.getAppendParams(
      `${this.apiConfig.getApiUrl()}${DASHBOARD_API_URL.DEFAULT_BUSINESS}`,
      replaceParams,
      payload,
    );

    return this.put(url, payload);
  }
  /**
   * Update the loop
   * @param payload : Api common payload object
   * @returns Observable<ApiResponse>
   */
  public updateDashboardWidgetSequence(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl()}${DASHBOARD_API_URL.UPDATE_WIDGET_SEQUENCE
      }`;

    return this.put(url, payload);
  }

  /**
   * create the Dashboard Widget
   * @param payload : Api common payload object
   * @returns Observable<ApiResponse>
   */
  public createDashboardWidget(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl()}${DASHBOARD_API_URL.CREATE_DASHBOARD_WIDGET
      }`;

    return this.post(url, payload);
  }



  /**
   * create the Dashboard Widget
   * @param payload : Api common payload object
   * @returns Observable<ApiResponse>
   */
  public getFraudTransaction(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl('dashboard')}${DASHBOARD_API_URL.FRAUD_TRANSACTION
      }`;

    return this.post(url, payload);
  }

  public getRealTimeStatus(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl('dashboard')}${DASHBOARD_API_URL.REAL_TIME_STATUS
      }`;

    return this.post(url, payload);
  }


  public getArchivedEmployees(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl('dashboard')}${DASHBOARD_API_URL.ARCHIVED_EMPLOYEES
      }`;

    return this.get(url);
  }

  public getMasterWidgetAllActive(): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl()}${DASHBOARD_API_URL.MASTER_WIDGET_ALL_ACTIVE}`;

    return this.get(url);
  }
  public getWorkingEmployees(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl('dashboard')}${DASHBOARD_API_URL.WORKING_EMPLOYEES
      }`;

    return this.post(url, payload);
  }
  public getLateEmployees(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl('dashboard')}${DASHBOARD_API_URL.LATE_EMPLOYEES
      }`;

    return this.post(url, payload);
  }
  public getNotScheduledUsers(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl('dashboard')}${DASHBOARD_API_URL.NOT_SCHEDULED_USERS
      }`;

    return this.post(url, payload);
  }
  public getRealTimeMap(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl('dashboard')}${DASHBOARD_API_URL.REAL_TIME_MAP
      }`;

    return this.post(url, payload);
  }
  public getOnLeaveUser(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl('dashboard')}${DASHBOARD_API_URL.ON_LEAVE
      }`;

    return this.post(url, payload);
  }
  public getOnBreakUser(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl('dashboard')}${DASHBOARD_API_URL.ON_BREAK
      }`;

    return this.post(url, payload);
  }
  public getUnavailableEmployee(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl('dashboard')}${DASHBOARD_API_URL.UNAVAILABLE_EMPLOYEE
      }`;

    return this.post(url, payload);
  }
  public getScheduleTimesheetCost(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl('dashboard')}${DASHBOARD_API_URL.SCHEDULE_TIMESHEET_COST
      }`;

    return this.post(url, payload);
  }
  public getScheduleTimesheetHours(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl('dashboard')}${DASHBOARD_API_URL.SCHEDULE_TIMESHEET_HOURS
      }`;

    return this.post(url, payload);
  }
  public getQrcodeTransactions(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl('dashboard')}${DASHBOARD_API_URL.QR_CODE_TRANSACTIONS
      }`;

    return this.post(url, payload);
  }
  public getOnLoop(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl('dashboard')}${DASHBOARD_API_URL.ON_LOOP
      }`;

    return this.post(url, payload);
  }

  public getLoopCheckIn(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl('dashboard')}${DASHBOARD_API_URL.LOOP_CHECK_IN}`;

    return this.post(url, payload);
  }

  public getLoopHistory(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl('dashboard')}${DASHBOARD_API_URL.LOOP_HISTORY
      }`;

    return this.post(url, payload);
  }
  public getUpcomingShifts(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl('dashboard')}${DASHBOARD_API_URL.UPCOMING_SHIFT
      }`;

    return this.post(url, payload);
  }
  public getEmployeePoints(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl('dashboard')}${DASHBOARD_API_URL.EMPLOYEE_POINTS
      }`;

    return this.post(url, payload);
  }
  public getHolidays(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl()}public-holiday/paging`;

    return this.post(url, payload);
  }
  public getUnassignedEmployee(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl('dashboard')}${DASHBOARD_API_URL.UNASSIGNED_EMPLOYEE
      }`;

    return this.get(url);
  }

  /**
   * Update the loop
   * @param payload : Api common payload object
   * @returns Observable<ApiResponse>
   */
  public updateDashboardWidget(payload: ApiPayload): Observable<ApiResponse> {
    const url = `${this.apiConfig.getApiUrl()}${DASHBOARD_API_URL.UPDATE_DASHBOARD_WIDGET
      }`;

    return this.put(url, payload);
  }


  /**
   * delete the loop
   * @param payload : Api common payload object
   * @returns Observable<ApiResponse>
   */
  public deleteDashboardWidget(payload: ApiPayload): Observable<ApiResponse> {
    const replaceParams = ['widget_id'];
    const url = APIUtil.getReplaceParams(
      `${this.apiConfig.getApiUrl()}${DASHBOARD_API_URL.DELETE_DASHBOARD_WIDGET}`,
      replaceParams,
      payload,
    );

    return this.delete(url);
  }

}

